import { useCallback, useEffect, useState } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  useBlockSlot,
  useProperty,
  SectionContextProvider,
  EditableToken,
} from '@vibesites/runtime/react';

function hasEngrainSightmap(property: Record<string, unknown> | null | undefined): boolean {
  if (!property) return false;
  return !!(
    String(property.custom_engrain_sightmap_url || '').trim() ||
    String(property.custom_engrain_sitemap_url || '').trim() ||
    String(property.custom_engrain_sightmap_id || '').trim() ||
    String(property.custom_engrain_sitemap_id || '').trim()
  );
}

/**
 * Interactive map wrapper with side gutters + click-to-scroll-trap.
 *
 * Prefers Engrain Sightmap when the property has a sightmap URL/ID; otherwise
 * falls back to ApartmentSync map. Full-bleed maps steal wheel/trackpad for
 * zoom, so gutters give a scroll lane and a click activates pointer events.
 */
export function InteractiveMapSection() {
  const property = useProperty() as Record<string, unknown>;
  const defaultBlock = hasEngrainSightmap(property) ? 'engrain-sitemap' : 'apartment-sync-map';
  const mapSlot = useBlockSlot('interactivemapsection.map_block', {
    defaultValue: defaultBlock,
  });
  const [mapActive, setMapActive] = useState(false);

  const activateMap = useCallback(() => setMapActive(true), []);
  const deactivateMap = useCallback(() => setMapActive(false), []);

  useEffect(() => {
    if (!mapActive) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMapActive(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mapActive]);

  return (
    <section
      className="interactive-map-section bg-[var(--vibe-color-background)] px-3 sm:px-5 md:px-6 pb-10 md:pb-14"
      onMouseLeave={deactivateMap}
    >
      <EditableToken
        path="interactivemapsection.map_block"
        as="div"
        className="interactive-map-section__frame relative min-h-[400px] md:min-h-[500px] flex flex-col overflow-hidden"
      >
        {mapSlot ? (
          <SectionContextProvider sectionId={mapSlot.sectionId}>
            <div
              className="min-h-[400px] md:min-h-[500px]"
              style={{ pointerEvents: mapActive ? 'auto' : 'none' }}
            >
              <mapSlot.BlockComponent sectionId={mapSlot.sectionId} />
            </div>
          </SectionContextProvider>
        ) : (
          <div className="interactive-map-section__empty">No block selected</div>
        )}

        {!mapActive && mapSlot ? (
          <button
            type="button"
            className="interactive-map-section__trap"
            onClick={activateMap}
            aria-label="Click to interact with the map"
          />
        ) : null}
      </EditableToken>

      <style>{`
        .interactive-map-section__empty {
          font-family: var(--vibe-font-body);
          color: var(--vibe-color-text-muted);
          text-align: center;
          padding: 2rem 1rem;
        }
        .interactive-map-section__trap {
          position: absolute;
          inset: 0;
          z-index: 2;
          border: 0;
          padding: 0;
          cursor: grab;
          background: transparent;
        }
        .interactive-map-section__trap:focus-visible {
          outline: 2px solid var(--vibe-color-primary, #111);
          outline-offset: -2px;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'InteractiveMapSection',
  displayName: 'Interactive Map Section',
  tokens: {
    'interactivemapsection.map_block': {
      label: 'Map Block',
      type: 'block',
      description:
        'Defaults to Engrain Sightmap when custom_engrain_sightmap_url (or ID) is set; otherwise ApartmentSync map.',
      defaultValue: 'apartment-sync-map',
      acceptedBlockCategories: ['Integrations'],
    },
  },
  slotGroups: {},
  collectionBindings: {},
};

(InteractiveMapSection as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default InteractiveMapSection;
